import { Fragment, useImperativeHandle, useEffect, useState } from 'react';
import { Grid, Button, Divider } from '@mui/material';
import withTranslations from '../../../utils/HighOrderComponent';
import ErrorLabel from '../ErrorLabel';
import { isUndefined } from 'lodash';
import {
  getItemsToChoose,
  getValueCurrentIndex,
  intersection,
  not,
  numberOfChecked,
  union,
} from './transfer-list-utils';
import TransferListItem from './TransferListItem';

const TransferListContainer = (props) => {
  const {
    t,
    name,
    error,
    itemsSelected = [],
    itemsToChoose = [],
    labelTextProp,
    targetEqualityCriteria,
    width,
    height,
    translations,
    disable,
    errorsHandler,
    transferRef,
  } = props || {};

  const [checked, setChecked] = useState([]);
  const [left, setLeft] = useState([]);
  const [right, setRight] = useState([]);

  const errorMessage = error?.[name];
  const hasError = !isUndefined(errorMessage);
  const leftChecked = intersection(checked, left);
  const rightChecked = intersection(checked, right);
  const choicesLabelText = translations?.Choices || t?.Choices;
  const chosenLabelText = translations?.Chosen || t?.Chosen;
  const selectedLabelText = translations?.selected || t?.selected;
  const size = { width: width || 250, height: height || 230 };
  const disabledComponentStyle = disable ? 'preview-style' : '';
  const smallSpace = width <= 200 ? 1 : 2;

  const onToggleHandler = (value) => () => {
    const currentIndex = getValueCurrentIndex(checked, value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
    hasError && errorRemovalHandler();
  };

  const errorRemovalHandler = () => {
    errorsHandler((prevError) => {
      delete prevError[name];
      return prevError;
    });
  };

  const onToggleAllHandler = (items) => () => {
    if (numberOfChecked(items, checked) === items?.length) {
      setChecked(not(checked, items));
    } else {
      setChecked(union(checked, items));
    }
    hasError && errorRemovalHandler();
  };

  const onCheckedRightHandler = () => {
    const rightList = right.concat(leftChecked);
    setRight(rightList);
    setLeft(not(left, leftChecked));
    setChecked(not(checked, leftChecked));
  };

  const onCheckedLeftHandler = () => {
    const leftList = not(right, rightChecked);
    setLeft(left.concat(rightChecked));
    setRight(leftList);
    setChecked(not(checked, rightChecked));
  };

  useEffect(() => {
    itemsToChoose &&
      setLeft(
        getItemsToChoose(itemsToChoose, itemsSelected, targetEqualityCriteria)
      );
  }, [itemsToChoose, itemsSelected, targetEqualityCriteria]);

  useEffect(() => {
    itemsSelected && setRight(itemsSelected);
  }, [itemsSelected]);

  useEffect(() => {
    disable && setChecked([]);
  }, [disable]);

  useImperativeHandle(transferRef, () => {
    return {
      getItemsLeft() {
        return left;
      },
      getItemsRight() {
        return right;
      },
    };
  });

  return (
    <Fragment>
      <Grid
        container
        spacing={smallSpace}
        justifyContent="center"
        alignItems="center"
        disabled
        className={disabledComponentStyle}
      >
        <Grid item>
          <Divider />
          <TransferListItem
            name={name}
            title={choicesLabelText}
            items={left}
            isLeft={true}
            checked={leftChecked}
            error={errorMessage}
            onToggleAll={onToggleAllHandler}
            selectedLabelText={selectedLabelText}
            size={size}
            onToggle={onToggleHandler}
            labelTextProp={labelTextProp}
            disable={disable}
          />
        </Grid>
        <Grid item disabled className={`${disabledComponentStyle}`}>
          <Grid container direction="column" alignItems="center">
            <Button
              sx={{ my: 0.5 }}
              variant="outlined"
              size="small"
              onClick={onCheckedRightHandler}
              disabled={leftChecked.length === 0}
              aria-label="move selected right"
            >
              &gt;
            </Button>
            <Button
              sx={{ my: 0.5 }}
              variant="outlined"
              size="small"
              onClick={onCheckedLeftHandler}
              disabled={rightChecked.length === 0}
              aria-label="move selected left"
            >
              &lt;
            </Button>
          </Grid>
        </Grid>
        <Grid item disabled className={disabledComponentStyle}>
          <Divider />
          <TransferListItem
            name={name}
            title={chosenLabelText}
            items={right}
            isLeft={false}
            checked={rightChecked}
            error={errorMessage}
            onToggleAll={onToggleAllHandler}
            selectedLabelText={selectedLabelText}
            size={size}
            onToggle={onToggleHandler}
            labelTextProp={labelTextProp}
            disable={disable}
          />
        </Grid>
      </Grid>
      <div className="col-md-11 position-relative mt-3 m-auto">
        <ErrorLabel error={errorMessage} />
      </div>
    </Fragment>
  );
};

export default withTranslations(TransferListContainer, 'TransferList');
